'use Admin'
import { useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown, FaTachometerAlt, FaUsers, FaBullhorn, FaBriefcase, FaGraduationCap, FaTools, FaHome, FaUser, FaProjectDiagram, FaFolderOpen } from "react-icons/fa";
import { useAuth } from "../AuthContext";
import { encryptId } from "../utils/crypto";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";

function SideBar({ isOpen, role }) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useLanguage();

  // Role accent
  const getRoleAccent = () => {
    if (role === 'admin') return 'linear-gradient(135deg, #6366f1, #8b5cf6)';
    if (role === 'professional') return 'linear-gradient(135deg, #10b981, #059669)';
    if (role === 'client') return 'linear-gradient(135deg, #f59e0b, #ec4899)';
    return 'var(--gradient-primary)';
  };

  const getRoleColor = () => {
    if (role === 'admin') return '#8b5cf6';
    if (role === 'professional') return '#10b981';
    if (role === 'client') return '#f59e0b';
    return '#00d4ff';
  };

  const getRoleLabel = () => {
    if (role === 'client') return t('business');
    if (role === 'professional') return t('professional');
    if (role === 'admin') return t('admin');
    return role;
  };

  const MenuItem = ({ to, icon: Icon, label, delay = 0, onClick, isButton = false }) => {
    const content = (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay }}
        whileHover={{ x: 6, transition: { duration: 0.2 } }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: '12px 20px',
          borderRadius: '12px',
          color: 'var(--text-secondary)',
          fontSize: '0.95rem',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          textDecoration: 'none',
          background: 'transparent',
          border: 'none',
          width: '100%',
          fontFamily: 'var(--font-body)',
          textAlign: 'left',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--bg-glass-hover)';
          e.currentTarget.style.color = 'var(--text-primary)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--text-secondary)';
        }}
      >
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'var(--bg-glass)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: getRoleColor(),
          fontSize: '1rem',
          transition: 'all 0.3s ease',
          flexShrink: 0,
        }}>
          <Icon />
        </div>
        <span>{label}</span>
      </motion.div>
    );

    if (isButton) {
      return <button onClick={onClick} style={{ all: 'unset', width: '100%', cursor: 'pointer' }}>{content}</button>;
    }

    return <Link to={to} style={{ textDecoration: 'none' }}>{content}</Link>;
  };

  const SubMenuItem = ({ to, icon: Icon, label, delay = 0 }) => (
    <motion.div
      initial={{ opacity: 0, x: -10, height: 0 }}
      animate={{ opacity: 1, x: 0, height: 'auto' }}
      exit={{ opacity: 0, x: -10, height: 0 }}
      transition={{ duration: 0.25, delay }}
    >
      <Link to={to} style={{ textDecoration: 'none' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 20px 10px 48px',
            borderRadius: '10px',
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = 'var(--text-primary)';
            e.currentTarget.style.background = 'var(--bg-glass)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = 'var(--text-muted)';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <Icon style={{ fontSize: '0.85rem', color: getRoleColor(), opacity: 0.7 }} />
          <span>{label}</span>
        </div>
      </Link>
    </motion.div>
  );

  const renderMenuItems = () => {
    if (role === "admin") {
      return (
        <>
          <MenuItem to="/AdminDashboard" icon={FaTachometerAlt} label={t("sidebarDashboard")} delay={0.1} />
          <MenuItem to="/userMange" icon={FaUsers} label={t("sidebarUserManagement")} delay={0.15} />
          <MenuItem to="/Ads" icon={FaBullhorn} label={t("sidebarAdvertisements")} delay={0.2} />
        </>
      );
    } else if (role === "professional") {
      return (
        <>
          <MenuItem to="/ProProject" icon={FaTachometerAlt} label={t("sidebarDashboard")} delay={0.1} />
          
          <MenuItem 
            isButton 
            onClick={() => setIsProfileOpen(!isProfileOpen)} 
            icon={FaUser} 
            label={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>{t("sidebarMyProfile")}</span>
                <motion.div
                  animate={{ rotate: isProfileOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FaChevronDown style={{ fontSize: '0.7rem', opacity: 0.5 }} />
                </motion.div>
              </div>
            } 
            delay={0.15} 
          />

          <AnimatePresence>
            {isProfileOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <SubMenuItem to={`/page/${encodeURIComponent(encryptId(user?.id || 0))}`} icon={FaUser} label={t("sidebarViewProfile")} delay={0.05} />
                <SubMenuItem to="/AddProject" icon={FaProjectDiagram} label={t("sidebarAddProjects")} delay={0.1} />
                <SubMenuItem to="/AddWorkExperience" icon={FaBriefcase} label={t("sidebarAddExperience")} delay={0.15} />
                <SubMenuItem to="/AddEducation" icon={FaGraduationCap} label={t("sidebarAddEducation")} delay={0.2} />
                <SubMenuItem to="/AddSkill" icon={FaTools} label={t("sidebarAddSkills")} delay={0.25} />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      );
    } else if (role === "client") {
      return (
        <>
          <MenuItem to="/BHome" icon={FaHome} label={t("sidebarHome")} delay={0.1} />
          <MenuItem to="/Profiles" icon={FaUser} label={t("sidebarProfiles")} delay={0.15} />
          <MenuItem to="/Projects" icon={FaProjectDiagram} label={t("sidebarProjects")} delay={0.2} />
          <MenuItem to="/MyProject" icon={FaFolderOpen} label={t("sidebarMyProject")} delay={0.25} />
        </>
      );
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(6, 6, 11, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 29,
            }}
          />

          {/* Sidebar */}
          <motion.aside
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100vh',
              width: '300px',
              background: 'var(--sidebar-bg)',
              backdropFilter: 'blur(30px) saturate(1.5)',
              WebkitBackdropFilter: 'blur(30px) saturate(1.5)',
              borderRight: '1px solid var(--border-glass)',
              zIndex: 30,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Sidebar Header */}
            <div style={{
              padding: '24px 20px',
              borderBottom: '1px solid var(--border-subtle)',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: getRoleAccent(),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-display)',
                  fontWeight: 800,
                  fontSize: '1.2rem',
                  color: '#fff',
                  boxShadow: `0 4px 15px ${getRoleColor()}40`,
                }}>D</div>
                <div>
                  <span style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    background: getRoleAccent(),
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>DevGenius</span>
                  <div style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: getRoleColor(),
                    marginTop: '2px',
                  }}>
                    {getRoleLabel()} {t("sidebarPanel")}
                  </div>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <nav style={{ padding: '16px 12px', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {renderMenuItems()}
              </div>
            </nav>

            {/* Bottom decoration */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid var(--border-subtle)',
            }}>
              <div style={{
                padding: '16px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${getRoleColor()}10, ${getRoleColor()}05)`,
                border: `1px solid ${getRoleColor()}20`,
              }}>
                <div style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                }}>
                  {t("sidebarLoggedInAs")}
                </div>
                <div style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)',
                  fontWeight: 600,
                  marginTop: '4px',
                }}>
                  {user?.name || t('userGeneric')}
                </div>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export default SideBar;